import type { AiOperationsRun, Locale } from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconBell,
  IconChecklist,
  IconChecks,
  IconSparkles,
  type TablerIcon,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@/components/ui/tabs";
import { usePermission } from "@/hooks/use-permission";
import { useWorkspaceDataStatus } from "@/hooks/use-workspace-data-status";
import { fetchAiOperationsRuns } from "@/lib/ai-run-api";
import { runDisplayStatus, runOutcome, runResultDetail } from "@/lib/ai-run-presentation";
import { canAccessApprovalQueue } from "@/lib/approval-access";
import { fetchApprovalProposals } from "@/lib/approval-api";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { type ReportingTimeZone, reportingDateInTimeZone } from "@/lib/reporting-time-zone";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useLocaleStore } from "@/stores/locale";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

/*
 * There is no notifications backend yet. This menu aggregates signals the
 * frontend can already read — pending approvals, shop data-access problems and
 * notable recent AI runs — and keeps read/unread state per user in
 * localStorage, so it does not sync across devices or browsers.
 */

type NotificationCategory = "approvals" | "aiRuns" | "account";
type NotificationTone = "warning" | "danger" | "info" | "neutral";
type NotificationTab = "all" | NotificationCategory;

interface NotificationItem {
  /** Stable per state: a new occurrence gets a new id and becomes unread again. */
  id: string;
  category: NotificationCategory;
  tone: NotificationTone;
  icon: TablerIcon;
  title: string;
  detail: string;
  at: string | null;
  open: (() => void) | null;
}

const AI_RUN_LOOKBACK = { type: "preset", value: "7d" } as const;
const MAX_AI_RUN_ITEMS = 20;
const READ_STATE_LIMIT = 300;
// Outcomes that need no attention would flood the list with daily routine runs.
const QUIET_RUN_OUTCOMES = new Set([
  "analyzing",
  "needsDecision",
  "noChanges",
  "completed",
  "observed",
  "rejected",
]);

const TONE_CLASS: Record<NotificationTone, string> = {
  warning: "bg-warning-background text-warning-foreground",
  danger: "bg-destructive-background text-destructive-foreground",
  info: "bg-info-background text-info-foreground",
  neutral: "bg-subtle text-text-secondary",
};

export function NotificationsMenu(): React.ReactElement {
  const { t } = useTranslation("layout");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<NotificationTab>("all");
  const navigate = useNavigate();
  const { hasModule } = usePermission();
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const locale = useLocaleStore((state) => state.locale);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const { items, sources, isLoading, isError, retry } = useNotificationItems();
  const { readIds, markRead } = useNotificationReadState(userId);

  const unread = items.filter((item) => !readIds.has(item.id));
  const unreadCount = unread.length;
  const tabs: NotificationTab[] = [
    "all",
    ...(["approvals", "aiRuns", "account"] as const).filter((category) => sources[category]),
  ];
  const activeTab = tabs.includes(tab) ? tab : "all";

  function openItem(item: NotificationItem): void {
    markRead([item.id]);
    if (!item.open) return;
    setOpen(false);
    item.open();
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label={
              unreadCount > 0
                ? t("notifications.triggerUnread", { count: unreadCount })
                : t("notifications.trigger")
            }
            className="relative flex size-8 shrink-0 items-center justify-center rounded-sm text-text-secondary outline-none transition-colors duration-150 ease-out hover:bg-state-hover hover:text-ink focus-visible:shadow-[0_0_0_1px_var(--color-focus-ring),0_0_0_4px_var(--color-focus-halo)] data-popup-open:bg-state-hover data-popup-open:text-ink motion-reduce:transition-none"
          />
        }
      >
        <IconBell className="size-[18px]" stroke={1.75} aria-hidden />
        {unreadCount > 0 ? (
          <span
            aria-hidden
            className="absolute top-0 -right-[3px] flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-micro font-semibold text-on-brand tabular-nums shadow-[0_0_0_2px_var(--color-surface)]"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverPopup
        side="bottom"
        align="end"
        className="w-[380px] max-w-[calc(100vw-2rem)] rounded-md *:data-[slot=popover-viewport]:p-0"
      >
        <div className="flex items-center justify-between px-3.5 pt-3 pb-2">
          <PopoverTitle className="text-base font-semibold text-ink">
            {t("notifications.title")}
          </PopoverTitle>
          <button
            type="button"
            disabled={unreadCount === 0}
            onClick={() => markRead(unread.map((item) => item.id))}
            className="flex h-7 shrink-0 items-center justify-center gap-1 rounded-sm px-2 text-xs font-medium text-text-secondary outline-none transition-colors hover:bg-state-hover hover:text-ink focus-visible:shadow-[0_0_0_1px_var(--color-focus-ring),0_0_0_4px_var(--color-focus-halo)] disabled:pointer-events-none disabled:opacity-60"
          >
            <span className="flex size-3.5 shrink-0 items-center justify-center">
              <IconChecks className="size-3.5" stroke={1.75} aria-hidden />
            </span>
            {t("notifications.markAllRead")}
          </button>
        </div>
        <Tabs
          value={activeTab}
          onValueChange={(value) => setTab(value as NotificationTab)}
          className="gap-0"
        >
          <TabsList className="w-full justify-start gap-4 border-line px-3.5 **:data-[slot=tab-indicator]:bg-ink">
            {tabs.map((value) => {
              const count =
                value === "all"
                  ? unreadCount
                  : unread.filter((item) => item.category === value).length;
              return (
                <TabsTab
                  key={value}
                  value={value}
                  className="h-9 px-0 font-normal text-text-secondary hover:text-ink data-active:font-medium data-active:text-ink"
                >
                  {t(`notifications.tabs.${value}`)}
                  {count > 0 ? (
                    <span
                      className={cn(
                        "flex h-4 items-center rounded-full px-[5px] text-micro font-semibold tabular-nums",
                        value === "approvals"
                          ? "bg-warning-background text-warning-foreground"
                          : "bg-subtle text-text-secondary",
                      )}
                    >
                      {count}
                    </span>
                  ) : null}
                </TabsTab>
              );
            })}
          </TabsList>
          {tabs.map((value) => {
            const visible =
              value === "all" ? items : items.filter((item) => item.category === value);
            return (
              <TabsPanel key={value} value={value} className="flex flex-col">
                {isError ? (
                  <div className="px-3.5 pt-2">
                    <ErrorState
                      variant="inline"
                      message={t("notifications.loadError")}
                      onRetry={retry}
                    />
                  </div>
                ) : null}
                {visible.length > 0 ? (
                  <ul className="flex max-h-[360px] flex-col overflow-y-auto p-1">
                    {visible.map((item) => (
                      <li key={item.id}>
                        <NotificationRow
                          item={item}
                          unread={!readIds.has(item.id)}
                          time={formatNotificationTime(item.at, timeZone, locale, t)}
                          onOpen={() => openItem(item)}
                        />
                      </li>
                    ))}
                  </ul>
                ) : isLoading ? (
                  <div
                    className="flex items-center justify-center py-10"
                    role="status"
                    aria-label={t("notifications.loading")}
                  >
                    <Spinner className="size-4 text-muted-foreground" />
                  </div>
                ) : (
                  <p className="px-3.5 py-10 text-center text-sm text-muted-foreground">
                    {t("notifications.empty")}
                  </p>
                )}
              </TabsPanel>
            );
          })}
        </Tabs>
        {hasModule("approvals") ? (
          <div className="flex h-10 shrink-0 items-center justify-center border-t border-line">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void navigate({ to: "/ai-operations", search: DEFAULT_APPROVAL_SEARCH });
              }}
              className="rounded-xs px-2 py-1 text-xs font-medium text-text-secondary outline-none transition-colors hover:text-ink focus-visible:shadow-[0_0_0_1px_var(--color-focus-ring),0_0_0_4px_var(--color-focus-halo)]"
            >
              {t("notifications.viewAll")}
            </button>
          </div>
        ) : null}
      </PopoverPopup>
    </Popover>
  );
}

function NotificationRow({
  item,
  unread,
  time,
  onOpen,
}: {
  item: NotificationItem;
  unread: boolean;
  time: string | null;
  onOpen: () => void;
}): React.ReactElement {
  const { t } = useTranslation("layout");
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-2.5 rounded-xs py-2.5 pr-3 pl-2.5 text-left outline-none transition-colors hover:bg-control-highlight focus-visible:bg-control-highlight focus-visible:shadow-[inset_0_0_0_1px_var(--color-focus-ring)]"
    >
      <span
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-xs",
          TONE_CLASS[item.tone],
        )}
      >
        <Icon className="size-3.5" stroke={1.75} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            "text-sm tabular-nums",
            unread ? "font-medium text-ink" : "text-text-secondary",
          )}
        >
          {item.title}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">{item.detail}</span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
        {time ? (
          <span className="text-badge text-muted-foreground tabular-nums">{time}</span>
        ) : null}
        <span className={cn("size-2 shrink-0 rounded-full", unread && "bg-brand")}>
          {unread ? <span className="sr-only">{t("notifications.unread")}</span> : null}
        </span>
      </span>
    </button>
  );
}

function useNotificationItems(): {
  items: NotificationItem[];
  sources: Record<NotificationCategory, boolean>;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
} {
  const { t } = useTranslation("layout");
  const { t: tAi } = useTranslation("aiOperations");
  const navigate = useNavigate();
  const { hasModule } = usePermission();
  const locale = useLocaleStore((state) => state.locale);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const user = useAuthStore((state) => state.user);
  const shops = useAuthStore((state) => state.shops);
  const workspaceId = user?.activeWorkspaceId ?? null;

  // Notifications are personal, so they span every workspace shop rather than
  // following the page-level analytics scope.
  const workspaceShopIds = useMemo(
    () => shops.filter((shop) => shop.workspaceId === workspaceId).map((shop) => shop.shopId),
    [shops, workspaceId],
  );
  const reviewableShopIds = useMemo(
    () => workspaceShopIds.filter((shopId) => canAccessApprovalQueue(shops, shopId)),
    [shops, workspaceShopIds],
  );
  const canSeeApprovals = hasModule("approvals") && reviewableShopIds.length > 0;
  const canSeeAiRuns = hasModule("approvals") && workspaceShopIds.length > 0;
  const canManageData = hasModule("settings");

  const approvalsQuery = useQuery({
    queryKey: ["notifications", "approvals", reviewableShopIds],
    queryFn: () =>
      fetchApprovalProposals({
        ...DEFAULT_APPROVAL_SEARCH,
        reviewStatus: "pending",
        sortBy: "expiresAt",
        sortOrder: "asc",
        pageSize: 100,
        shopIds: reviewableShopIds.join(","),
      }),
    enabled: canSeeApprovals,
    staleTime: 30_000,
  });
  const runsQuery = useQuery({
    queryKey: ["notifications", "ai-runs", workspaceShopIds, timeZone],
    queryFn: () => fetchAiOperationsRuns(AI_RUN_LOOKBACK, timeZone, workspaceShopIds),
    enabled: canSeeAiRuns,
    staleTime: 60_000,
  });
  const dataStatusQuery = useWorkspaceDataStatus(workspaceId);

  const items = useMemo(() => {
    const result: NotificationItem[] = [];

    const approvals = approvalsQuery.data;
    const pending = approvals?.summary.pending ?? 0;
    if (approvals && pending > 0) {
      const latestCreatedAt = maxIso(approvals.data.map((proposal) => proposal.createdAt));
      const earliestExpiry = minIso(approvals.data.map((proposal) => proposal.expiresAt));
      const shopCount = new Set(approvals.data.map((proposal) => proposal.shopId).filter(Boolean))
        .size;
      result.push({
        id: `approvals:${latestCreatedAt ?? pending}`,
        category: "approvals",
        tone: "warning",
        icon: IconChecklist,
        title: t("notifications.approvals.title", { count: pending }),
        detail: [
          earliestExpiry
            ? t("notifications.approvals.earliestExpiry", {
                relative: formatRelative(earliestExpiry, locale),
              })
            : null,
          shopCount > 1 ? t("workspace.shopCount", { count: shopCount }) : null,
        ]
          .filter(Boolean)
          .join(" · "),
        at: latestCreatedAt,
        open: () => void navigate({ to: "/ai-operations", search: DEFAULT_APPROVAL_SEARCH }),
      });
    }

    for (const shop of dataStatusQuery.data?.shops ?? []) {
      if (!shop.sources.some((source) => source.state === "action_required")) continue;
      const lastSuccess = maxIso(shop.sources.map((source) => source.lastSuccessAt));
      result.push({
        id: `account:${shop.shopId}:${lastSuccess ?? "never"}`,
        category: "account",
        tone: "danger",
        icon: IconAlertTriangle,
        title: t("notifications.account.actionRequiredTitle", { shop: shop.shopName }),
        detail: t(
          canManageData
            ? "notifications.account.actionRequiredDetail"
            : "notifications.account.actionRequiredReadOnly",
          { marketplace: shop.marketplaceName },
        ),
        // The data-status payload carries no "since" timestamp for the problem.
        at: null,
        open: canManageData
          ? () => void navigate({ to: "/settings", search: { tab: "amazon", dataDetails: true } })
          : null,
      });
    }

    const notableRuns = (runsQuery.data ?? [])
      .filter((run): run is AiOperationsRun & { finishedAt: string } => run.finishedAt !== null)
      .map((run) => ({ run, status: runDisplayStatus(run, false) }))
      .filter(
        ({ status }) =>
          status.incident !== null ||
          (status.outcome !== null && !QUIET_RUN_OUTCOMES.has(status.outcome)),
      )
      .sort((a, b) => Date.parse(b.run.finishedAt) - Date.parse(a.run.finishedAt))
      .slice(0, MAX_AI_RUN_ITEMS);
    for (const { run, status } of notableRuns) {
      const subject =
        run.managedGroupName ?? run.campaignName ?? tAi("history.row.removedCampaign");
      result.push({
        id: `run:${run.id}:${status.outcome ?? ""}:${status.incident ?? ""}`,
        category: "aiRuns",
        tone: status.incident
          ? "danger"
          : status.outcome === "executed" || status.outcome === "recommended"
            ? "info"
            : "neutral",
        icon: status.incident ? IconAlertTriangle : IconSparkles,
        title: `${subject} · ${runOutcome(run, false, tAi).label}`,
        detail: [runResultDetail(run, 0, tAi), run.shopName].filter(Boolean).join(" · "),
        at: run.finishedAt,
        open: () => void navigate({ to: "/ai-operations/$runId", params: { runId: run.id } }),
      });
    }

    // Undated attention items (account access) stay pinned above dated ones.
    return result.sort(
      (a, b) => (b.at ? Date.parse(b.at) : Infinity) - (a.at ? Date.parse(a.at) : Infinity),
    );
  }, [
    approvalsQuery.data,
    dataStatusQuery.data,
    runsQuery.data,
    canManageData,
    locale,
    navigate,
    t,
    tAi,
  ]);

  const queries = [
    canSeeApprovals ? approvalsQuery : null,
    canSeeAiRuns ? runsQuery : null,
    workspaceId ? dataStatusQuery : null,
  ].filter((query) => query !== null);

  return {
    items,
    sources: {
      approvals: canSeeApprovals,
      aiRuns: canSeeAiRuns,
      account: Boolean(workspaceId),
    },
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
    retry: () => {
      for (const query of queries) if (query.isError) void query.refetch();
    },
  };
}

function useNotificationReadState(userId: string | null): {
  readIds: ReadonlySet<string>;
  markRead: (ids: string[]) => void;
} {
  const storageKey = userId ? `adpraxis.notifications.read.${userId}` : null;
  const [state, setState] = useState<{ key: string | null; ids: string[] }>(() => ({
    key: storageKey,
    ids: loadReadIds(storageKey),
  }));
  // Re-read when the signed-in user changes (render-phase sync, no effect).
  const current =
    state.key === storageKey ? state : { key: storageKey, ids: loadReadIds(storageKey) };
  if (current !== state) setState(current);

  const markRead = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setState((previous) => {
      const merged = [...previous.ids.filter((id) => !ids.includes(id)), ...ids].slice(
        -READ_STATE_LIMIT,
      );
      saveReadIds(previous.key, merged);
      return { key: previous.key, ids: merged };
    });
  }, []);

  const readIds = useMemo(() => new Set(current.ids), [current.ids]);
  return { readIds, markRead };
}

function loadReadIds(key: string | null): string[] {
  if (!key) return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function saveReadIds(key: string | null, ids: string[]): void {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Storage can be unavailable (private mode, quota); read state is then session-only.
  }
}

function maxIso(values: readonly (string | null | undefined)[]): string | null {
  let result: string | null = null;
  for (const value of values) {
    if (value && (result === null || Date.parse(value) > Date.parse(result))) result = value;
  }
  return result;
}

function minIso(values: readonly (string | null | undefined)[]): string | null {
  let result: string | null = null;
  for (const value of values) {
    if (value && (result === null || Date.parse(value) < Date.parse(result))) result = value;
  }
  return result;
}

function formatRelative(iso: string, locale: Locale): string {
  const diffMs = Date.parse(iso) - Date.now();
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 60) return format.format(minutes, "minute");
  const hours = Math.round(diffMs / 3_600_000);
  if (Math.abs(hours) < 48) return format.format(hours, "hour");
  return format.format(Math.round(diffMs / 86_400_000), "day");
}

/** Today → clock time, yesterday → label, older → short date; in the reporting time zone. */
function formatNotificationTime(
  iso: string | null,
  timeZone: ReportingTimeZone,
  locale: Locale,
  t: TFunction<"layout">,
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const now = new Date();
  const day = reportingDateInTimeZone(date, timeZone);
  if (day === reportingDateInTimeZone(now, timeZone)) {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(date);
  }
  const yesterday = reportingDateInTimeZone(new Date(now.getTime() - 86_400_000), timeZone);
  if (day === yesterday) return t("notifications.yesterday");
  return new Intl.DateTimeFormat(locale, { timeZone, month: "short", day: "numeric" }).format(date);
}
